import React from 'react'

import moment from 'moment'
import { DateTime } from '../../domain/DateTime'
import { TimelineSpan, TimelineSpanParent } from '../../domain/TimelineSpan'
import { BottleneckIdentifications, TraceSpanCategory } from '../../domain/Trace'
import CodeCover from '../../helper/code'
import Link from '../../helper/link'
import Memory from '../../helper/memory'
import Milliseconds from '../../helper/milliseconds'
import Percent from '../../helper/percent'
import Shorten from '../../helper/shorten'
import { Col, Row } from '../../ui/grid'
import { connect } from 'react-redux'
import { ApplicationState } from '../../redux/ApplicationState'
import { TraceDetailsStacktrace } from './TraceDetailsStacktrace'
import { CurlErrno } from './CurlErrno'
import { HttpStatus } from './HttpStatus'
import { CodeBlock } from '../../ui/codeBlock'
import { Alert } from '../../ui/alert'
import { redisSpanIsMissingKeyAnnotation } from '../../domain/Span'
import { ParentSpansList } from './ParentSpansList'

export interface SpanTraceInfo {
    traceId: string
    isChildTrace: boolean
    serverName: string
    service: string
    environment: string
    measureDate: DateTime
}

interface OwnProps {
    wt: number
    span: TimelineSpan
    traceInfo?: SpanTraceInfo
    parents: TimelineSpanParent[]
    categories: { [category: string]: TraceSpanCategory }
    mc?: number
    route: string
    showSettingsLink: boolean
    bottleneckRoute: string
    isBottleneckDetails: boolean
    operationId?: number
    bottleneckIdentifications?: BottleneckIdentifications
    isRoot?: boolean
}

interface StateProps {
    shellDetailsActivated: boolean
    includesDynamicallyInstrumentedFunctions: boolean
}

type Props = OwnProps & StateProps

class TraceDetailsSpan extends React.Component<Props> {
    public static defaultProps = {
        parents: [],
        showSettingsLink: true,
        isBottleneckDetails: false,
        isRoot: false,
    }

    public blacklist = [
        'repeat_count',
        'repeat_wt',
        'gc',
        'gcc',
        'cwt',
        'cct',
        'query',
        'err_exception',
        'err_source',
        'err_msg',
        'err_trace',
        'cpu',
        'sql',
        'path',
        'service',
        'method',
        'php',
        'php.exit_status',
        'namespace',
        'net.in',
        'net.out',
        'mem',
        'fn',
        'server.cpu_percent',
        'server.mem_percent',
        'server.load_avg',
        'swdal.entity',
        'truncated',
        'sql.params',
        'cmd',
        'http.url',
        'http.method',
        'http.host',
        'http.location',
        'http.status',
        'curl.namelookup',
        'curl.errno',
        'curl.connect',
        'curl.appconnect',
        'curl.pretransfer',
        'curl.starttransfer',
        'curl.redirect',
        'graphql.query',
        'm.fio',
        'm.ct',
        'm.al',
        'm.alio',
        'm.alct',
        'm.apcu',
        'xdebug',
        'kafka.payload_size',
        'kafka.topic',
        'user_agent',
        'session.save_handler',
        'session.save_handler.user_class',
        'shopware.http_cache.context_cookie',
        'shopware.http_cache.key',
        'shopware.http_cache.tags',
        'driver',
        'mysql.index_usage',
    ]

    public render() {
        const { span, includesDynamicallyInstrumentedFunctions } = this.props
        const blacklist = this.blacklist
        const annotations = span.annotations || {}

        return (
            <div className="box-body">
                {this.props.traceInfo && this.props.traceInfo.isChildTrace ? (
                    <>
                        <div className="row">
                            <div className="col-md-12">Distributed Trace</div>
                        </div>
                        <div className="row">
                            <div className="col-md-12">
                                <small>
                                    This span belongs to a{' '}
                                    <Link
                                        className="profile-list-item"
                                        route={'XhprofProfilerBundle.Profile.quick'}
                                        params={{
                                            profile: this.props.traceInfo.traceId,
                                            s: this.props.traceInfo.service,
                                            env: this.props.traceInfo.environment,
                                            measured: moment.utc(this.props.traceInfo.measureDate.date).unix(),
                                        }}
                                    >
                                        child trace
                                    </Link>{' '}
                                    request served by <code>{this.props.traceInfo.serverName}</code>
                                </small>
                            </div>
                        </div>
                    </>
                ) : null}

                {span.hasBottleneck && !this.props.isBottleneckDetails ? (
                    <div className="row">
                        <div className="col-md-12">
                            <Alert level="warning" compact additionalClasses="text-ellipsis">
                                <i className="fa fa-warning" /> We consider this call a{' '}
                                <Link
                                    route={this.props.bottleneckRoute}
                                    params={{ bottleneck: span.bottlenecks ? span.bottlenecks[0] ?? undefined : undefined }}
                                >
                                    bottleneck (see description for details)
                                </Link>
                                .
                            </Alert>
                        </div>
                    </div>
                ) : null}
                {span.name === 'Transaction' && span.cat === 'sql' ? (
                    <Alert compact level="info">
                        This span represents a database transaction from <code>BEGIN</code> to <code>COMMIT</code>/<code>ROLLBACK</code>.
                    </Alert>
                ) : null}

                {'truncated' in annotations ? (
                    <div className="row">
                        <div className="col-md-12">
                            <small>
                                Notice: All annotations of this call were truncated in the tracing extension, because this trace contains
                                more than the allowed number of spans (1500).
                            </small>
                        </div>
                    </div>
                ) : null}

                {'repeat_count' in annotations ? (
                    <div className="row">
                        <div className="col-md-6">
                            <small>Duration</small>
                            <br />
                            <Milliseconds value={+annotations.repeat_wt} />
                        </div>
                        <div className="col-md-6">
                            <small>Number of Calls</small>
                            <br />
                            <big>{annotations.repeat_count}x</big>
                        </div>
                    </div>
                ) : (
                    <div className="row">
                        <div className="col-md-6">
                            <small>Started at</small>
                            <br />
                            <Milliseconds value={+span.cs} showNoValueAsHyphen={false} />
                        </div>
                        <div className="col-md-6">
                            <small>Duration</small>
                            <br />
                            <Milliseconds value={+span.wt} />
                            <small>
                                {' '}
                                (<Percent value={span.wt / this.props.wt} />)
                            </small>
                        </div>
                    </div>
                )}
                {'php.exit_status' in annotations ? (
                    <div className="row">
                        <div className="col-md-12">
                            <small>
                                <i className="fa fa-bolt" /> Script stopped with a non-zero exit status
                            </small>
                        </div>
                        <div className="col-md-12">
                            <strong>{annotations['php.exit_status']}</strong>
                        </div>
                    </div>
                ) : null}

                {span.ms && span.mc ? (
                    <div className="row">
                        <div className="col-md-6">
                            <small>Memory Start</small>
                            <br />
                            <Memory value={+span.ms} />
                        </div>
                        <div className="col-md-6">
                            <small>Memory Change</small>
                            <br />
                            <Memory value={+span.mc} />{' '}
                            <small>
                                (<Percent value={this.props.mc ? span.mc / this.props.mc : undefined} />)
                            </small>
                        </div>
                    </div>
                ) : null}

                <div className="row">
                    {'cct' in annotations ? (
                        <div className="col-md-6">
                            <small>Included Files</small>
                            <br />
                            {annotations.cct}
                        </div>
                    ) : null}

                    {'cwt' in annotations ? (
                        <div className="col-md-6">
                            <small>Compile Overhead</small>
                            <br />
                            <Milliseconds value={+annotations.cwt} />
                            <small>
                                {' '}
                                (<Percent value={(annotations.cwt as number) / span.wt} />)
                            </small>
                        </div>
                    ) : null}

                    {'gc' in annotations ? (
                        <div className="col-md-6">
                            <small>Garbage Collection</small>
                            <br />
                            {annotations.gc}x
                        </div>
                    ) : null}

                    {'mem' in annotations ? (
                        <div className="col-md-6">
                            <small>Memory</small>
                            <br />
                            <Memory value={1024 * (annotations.mem as number)} />
                        </div>
                    ) : null}

                    {'net.out' in annotations ? (
                        <div className="col-md-6">
                            <small>Network (outgoing)</small>
                            <br />
                            <Memory value={1 * (annotations['net.out'] as number)} class="http" />
                        </div>
                    ) : null}

                    {'net.in' in annotations ? (
                        <div className="col-md-6">
                            <small>Network (incoming)</small>
                            <br />
                            <Memory value={1 * (annotations['net.in'] as number)} class="http" />
                        </div>
                    ) : null}
                </div>

                {'err_exception' in annotations ? (
                    <div className="row">
                        <div className="col-md-12">
                            <small>
                                <i className="fa fa-bolt" /> Request was aborted with an error
                            </small>
                        </div>
                        <div className="col-md-12">
                            <strong>{annotations.err_exception}</strong>
                            {'err_msg' in annotations ? <p>{annotations.err_msg}</p> : null}
                        </div>
                        {'err_source' in annotations ? (
                            <div className="col-md-12">
                                <small>{annotations.err_source}</small>
                            </div>
                        ) : null}
                    </div>
                ) : null}

                {'cmd' in annotations ? (
                    <div className="row">
                        <div className="col-md-12">
                            <small>Command</small>
                        </div>
                        <div className="col-md-12">
                            <CodeCover type="cmd" code={annotations.cmd as string} />
                            {!this.props.shellDetailsActivated && (
                                <div className="alert alert-info">
                                    This application has Shell details setting deactivated, so only commands are shown.{' '}
                                    {this.props.showSettingsLink ? (
                                        <Link route={'XhprofOrganizationBundle.Application.settings'}>See Settings</Link>
                                    ) : null}
                                </div>
                            )}
                        </div>
                    </div>
                ) : null}

                {'sql' in annotations ? (
                    <div className="row">
                        <div className="col-md-12">
                            <small>SQL</small>
                        </div>
                        <div className="col-md-12">
                            <CodeCover type="sql" code={annotations.sql as string} driver={annotations.driver} />
                        </div>
                    </div>
                ) : span.cat === 'sql' && span.name !== 'Transaction' ? (
                    <div className="row">
                        <div className="col-md-12">
                            <small>SQL</small>
                        </div>
                        <div className="col-md-12">
                            {span.name === 'unknown' ? (
                                <div className="alert alert-warning">
                                    This SQL query could not be anonymized on the Tideways daemon and was therefore not transmitted to the
                                    backend. You can help us improve our SQL anonymizer by{' '}
                                    <a
                                        href={
                                            'https://support.tideways.com/documentation/reference/daemon/log-parser-errors.html?utm_medium=app&utm_source=trace-details&utm_campaign=sql-anonymizer-help'
                                        }
                                    >
                                        following these debug steps
                                    </a>
                                    {'.'}
                                </div>
                            ) : (
                                <div className="alert alert-info">
                                    This application has SQL details setting deactivated, so only statement type and tables are shown.{' '}
                                    {this.props.showSettingsLink ? (
                                        <Link route={'XhprofOrganizationBundle.Application.settings'}>See Settings</Link>
                                    ) : null}
                                </div>
                            )}
                        </div>
                    </div>
                ) : null}

                {'driver' in annotations ? (
                    <div className="row">
                        <div className="col-md-6">
                            <small>Driver</small>
                            <br />
                            <small>{annotations.driver}</small>
                        </div>
                        {'mysql.index_usage' in annotations ? (
                            <div className="row">
                                <div className="col-md-6">
                                    <small>Index Usage</small>
                                    <br />
                                    <span
                                        className={
                                            annotations['mysql.index_usage'] == 'none' || annotations['mysql.index_usage'] == 'bad'
                                                ? 'tw-tooltip tw-tooltip--right'
                                                : ''
                                        }
                                    >
                                        {annotations['mysql.index_usage'] == 'none' || annotations['mysql.index_usage'] == 'bad' ? (
                                            <>
                                                <em>{annotations['mysql.index_usage']}</em>
                                                {annotations['mysql.index_usage'] === 'none' ? (
                                                    <span className="tw-tooltip-text">
                                                        No index was used for this query.
                                                        <br />
                                                        If this query was slow, consider adding an index on columns
                                                        <br />
                                                        in the WHERE, ORDER BY and GROUP BY clauses.
                                                        <br />
                                                        This might help speed it up significantly.
                                                    </span>
                                                ) : annotations['mysql.index_usage'] === 'bad' ? (
                                                    <span className="tw-tooltip-text">
                                                        An inefficient index is being used for this query.
                                                        <br />
                                                        Only a part of the condition is covered by indexed columns.
                                                        <br />
                                                        The query could be faster with an index covering more columns
                                                        <br />
                                                        that this query uses in WHERE, ORDER BY or GROUP BY clauses.
                                                    </span>
                                                ) : null}
                                            </>
                                        ) : (
                                            annotations['mysql.index_usage']
                                        )}
                                    </span>
                                </div>
                            </div>
                        ) : null}
                    </div>
                ) : null}

                {'graphql.query' in annotations ? (
                    <div className="row">
                        <div className="col-md-12">
                            <small>Query</small>
                        </div>
                        <div className="col-md-12">
                            <CodeCover type="graphql" code={annotations['graphql.query'] as string} />
                        </div>
                    </div>
                ) : span.cat === 'graphql' ? (
                    <div className="row">
                        <div className="col-md-12">
                            <small>Query</small>
                        </div>
                        <div className="col-md-12">
                            <div className="alert alert-info">
                                This application has GraphQL query details setting deactivated, so only statement type and tables are shown.{' '}
                                {this.props.showSettingsLink ? (
                                    <Link route={'XhprofOrganizationBundle.Application.settings'}>See Settings</Link>
                                ) : null}
                            </div>
                        </div>
                    </div>
                ) : null}

                <div className="row">
                    {'kafka.topic' in annotations ? (
                        <div className="col-md-6">
                            <small>Topic</small>
                            <br />
                            {annotations['kafka.topic']}
                        </div>
                    ) : null}

                    {'kafka.payload_size' in annotations ? (
                        <div className="col-md-6">
                            <small>Message Size</small>
                            <br />
                            <Memory value={annotations['kafka.payload_size']} class="" />
                        </div>
                    ) : null}
                </div>

                {'sql.params' in annotations ? (
                    <div>
                        <div className="row">
                            <div className="col-md-12">
                                <small>SQL Params</small>
                            </div>
                        </div>
                        <div>
                            {(annotations['sql.params'] as any).map((param, key) => {
                                return (
                                    <div className="row" key={'sql.param' + key}>
                                        <div className="col-md-2">{key}:</div>
                                        <div className="col-md-10">{param}</div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                ) : null}

                <div className="row">
                    {'http.method' in annotations ? (
                        <div className="col-md-6">
                            <small>Method</small>
                            <br />
                            <span title={annotations['http.method']}>{annotations['http.method']}</span>
                        </div>
                    ) : null}
                    {'http.host' in annotations ? (
                        <div className="col-md-6">
                            <small>Host</small>
                            <br />
                            <span title={annotations['http.host']}>{annotations['http.host']}</span>
                        </div>
                    ) : null}
                </div>

                {'http.url' in annotations ? (
                    /* This one is on the root span. */
                    <div className="row">
                        <div className="col-md-12">
                            <small>Path</small>
                            <br />
                            <CodeCover code={annotations['http.url'] as string} />
                        </div>
                    </div>
                ) : null}

                {'path' in annotations && annotations.path ? (
                    /* This one is on HTTP spans. */
                    <div className="row">
                        <div className="col-md-12">
                            <small>Path</small>
                        </div>
                        <div className="col-md-12">
                            <CodeCover code={annotations.path as string} />
                        </div>
                    </div>
                ) : null}

                {'query' in annotations && annotations.query ? (
                    /* This is on both the root span and HTTP spans. */
                    <div className="row">
                        <div className="col-md-12">
                            <small>Query</small>
                        </div>
                        <div className="col-md-12">
                            <CodeCover code={annotations.query as string} />
                        </div>
                    </div>
                ) : null}

                {'http.status' in annotations ? (
                    <div className="row">
                        <div className="col-md-12">
                            <small>HTTP Status Code</small>
                            <br />
                            <HttpStatus code={annotations['http.status']} />
                        </div>
                    </div>
                ) : null}

                {'http.location' in annotations ? (
                    <div className="row">
                        <div className="col-md-12">
                            <small>Redirect</small>
                            <br />
                            <CodeCover code={annotations['http.location'] as string} />
                        </div>
                    </div>
                ) : null}

                {'user_agent' in annotations ? (
                    <div className="row">
                        <div className="col-md-12">
                            <small>User Agent</small>
                            <br />
                            <CodeCover code={annotations['user_agent'] as string} />
                        </div>
                    </div>
                ) : null}

                {'session.save_handler' in annotations ? (
                    <div className="row">
                        <div className="col-md-12">
                            <small>Session Save Handler</small>
                            <br />
                            {annotations['session.save_handler']}
                            {'session.save_handler.user_class' in annotations ? (
                                <ul className="list-unstyled tw-ml-8">
                                    {annotations['session.save_handler.user_class']
                                        .split(';')
                                        .map(userClass => userClass.trim())
                                        .map((userClass, index) => (
                                            <li title={userClass} key={userClass}>
                                                <div className="text-ellipsis">
                                                    {index === 0 ? null : <>↳</>} {userClass}
                                                </div>
                                            </li>
                                        ))}
                                </ul>
                            ) : null}
                        </div>
                    </div>
                ) : null}

                {'curl.namelookup' in annotations ? (
                    <div className="row">
                        <div className="col-md-6">
                            <small>cURL Name Lookup</small>
                            <br />
                            <Milliseconds value={+annotations['curl.namelookup']} />
                        </div>
                        {'curl.connect' in annotations ? (
                            <div className="col-md-6">
                                <small>cURL Connect</small>
                                <br />
                                <Milliseconds value={+annotations['curl.connect']} />
                            </div>
                        ) : null}
                        {'curl.appconnect' in annotations ? (
                            <div className="col-md-6">
                                <small>cURL AppConnect</small>
                                <br />
                                <Milliseconds value={+annotations['curl.appconnect']} />
                            </div>
                        ) : null}
                        {'curl.pretransfer' in annotations ? (
                            <div className="col-md-6">
                                <small>cURL PreTransfer</small>
                                <br />
                                <Milliseconds value={+annotations['curl.pretransfer']} />
                            </div>
                        ) : null}
                        {'curl.starttransfer' in annotations ? (
                            <div className="col-md-6">
                                <small>cURL StartTransfer</small>
                                <br />
                                <Milliseconds value={+annotations['curl.starttransfer']} />
                            </div>
                        ) : null}
                        {'curl.redirect' in annotations ? (
                            <div className="col-md-6">
                                <small>cURL Redirect</small>
                                <br />
                                <Milliseconds value={+annotations['curl.redirect']} />
                            </div>
                        ) : null}
                        {'curl.errno' in annotations ? (
                            <div className="col-md-6">
                                <small>cURL Error</small>
                                <br />
                                <CurlErrno errno={annotations['curl.errno']} />
                            </div>
                        ) : null}
                    </div>
                ) : null}

                {'shopware.http_cache.key' in annotations ? (
                    <div className="row">
                        <div className="col-md-12">
                            <small>HTTP Cache Key</small>
                            <br />
                            <span title={annotations['shopware.http_cache.key']}>{annotations['shopware.http_cache.key']}</span>
                        </div>
                    </div>
                ) : null}

                {'shopware.http_cache.tags' in annotations ? (
                    <div className="row">
                        <div className="col-md-12">
                            <small>HTTP Cache Tags</small>
                            <br />
                            <CodeBlock code={annotations['shopware.http_cache.tags'] as string} />
                        </div>
                    </div>
                ) : null}

                {'shopware.http_cache.context_cookie' in annotations ? (
                    <div className="row">
                        <div className="col-md-12">
                            <small>HTTP Cache Context Cookie</small>
                            <br />
                            <CodeBlock code={annotations['shopware.http_cache.context_cookie'] as string} language="yaml" />
                        </div>
                    </div>
                ) : null}

                <div className="row">
                    {Object.entries(annotations).map(([key, annotation]) => {
                        if (typeof annotation !== 'string' && !((annotation as any) instanceof String)) {
                            return null
                        }

                        return blacklist.includes(key) ? null : (
                            <div key={key} className="col-md-6 text-ellipsis">
                                <small>{key}</small>
                                <br />
                                <span title={annotation as string}>{annotation}</span>
                            </div>
                        )
                    })}
                </div>

                {span.cat === 'app' ? (
                    <div className="row">
                        {'server.cpu_percent' in annotations && annotations['server.cpu_percent'] ? (
                            <div>
                                <div className="col-md-12 centered">
                                    <strong>Server metrics at the time of trace collection</strong>
                                </div>
                                <div className="col-md-6">
                                    <small>CPU Percentage</small>
                                    <br />
                                    <Percent value={(annotations['server.cpu_percent'] as number) / 100} />
                                </div>
                            </div>
                        ) : null}
                        {'server.mem_percent' in annotations && annotations['server.mem_percent'] ? (
                            <div className="col-md-6">
                                <small>Used Memory Percentage</small>
                                <br />
                                <Percent value={(annotations['server.mem_percent'] as number) / 100} />
                            </div>
                        ) : null}
                        {'server.load_avg' in annotations && annotations['server.load_avg'] ? (
                            <div className="col-md-6">
                                <small>Load Average</small>
                                <br />
                                {annotations['server.load_avg']}
                            </div>
                        ) : null}
                        <div
                            className="col-md-12 text-ellipsis custom-context-info"
                            title="See how you can add custom context variables to each trace."
                        >
                            <a href="https://support.tideways.com/documentation/reference/php-extension/custom-metadata.html">
                                See how you can add custom context variables to each trace.
                            </a>
                        </div>
                    </div>
                ) : null}

                {redisSpanIsMissingKeyAnnotation(span) ? (
                    <Alert level="info" compact additionalClasses="tw-mt-8">
                        To collect keys of Redis operations, see{' '}
                        <a
                            href="https://support.tideways.com/documentation/setup/configuration/advanced-instrumentation.html#collect-keys-for-redis-instrumentation"
                            target="_blank"
                            rel="noreferrer"
                        >
                            tideways.features.redis_keys
                        </a>{' '}
                        configuration setting
                    </Alert>
                ) : null}

                <ParentSpansList parents={this.props.parents} categories={this.props.categories} route={this.props.route} />

                {span.relatedTraces ? (
                    <Row>
                        <Col>
                            <h4>Traces originating from here</h4>
                        </Col>
                        <Col>
                            <ul className="list-unstyled">
                                {span.relatedTraces.map((relatedTrace, index) => {
                                    return (
                                        <li key={index}>
                                            <Link route={'XhprofProfilerBundle.Profile.quick'} query={{ profile: relatedTrace.id }}>
                                                <Shorten string={relatedTrace.title} />
                                            </Link>
                                        </li>
                                    )
                                })}
                            </ul>
                        </Col>
                    </Row>
                ) : null}

                {span.stack && span.stack.length > 0 && !this.props.isBottleneckDetails ? (
                    <TraceDetailsStacktrace stack={span.stack} />
                ) : null}
            </div>
        )
    }
}

export default connect(
    (globalState: ApplicationState): StateProps => {
        return {
            shellDetailsActivated: globalState.tidewaysContext.application?.shellDetailsActivated,
            includesDynamicallyInstrumentedFunctions: globalState.tidewaysContext.application?.includesDynamicallyInstrumentedFunctions,
        }
    },
)(TraceDetailsSpan)
